// src/pages/Summary.js

import React, { useState, useEffect, useContext } from 'react';
import Papa from 'papaparse';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TableSortLabel,
  CircularProgress,
} from '@mui/material';

import { AuthContext } from '../context/AuthContext';

// Helpers for stable sorting
function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) return -1;
  if (b[orderBy] > a[orderBy]) return 1;
  return 0;
}
function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}
function stableSort(array, comparator) {
  const stabilized = array.map((el, idx) => [el, idx]);
  stabilized.sort((a, b) => {
    const cmp = comparator(a[0], b[0]);
    return cmp !== 0 ? cmp : a[1] - b[1];
  });
  return stabilized.map((el) => el[0]);
}

const Summary = () => {
  // determine partner
  const { user } = useContext(AuthContext);
  const domain = user?.email.split('@')[1]?.toLowerCase() || '';
  let partnerFilter = '';
  if (domain === 'merchantsbonding.com') partnerFilter = 'merchants';
  else if (domain === 'colonialsurety.com') partnerFilter = 'colonial';
  else if (domain === 'everestglobal.com') partnerFilter = 'everest';

  // sort state
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('bondNumber');

  // primary CSV
  const [bondData, setBondData] = useState([]);
  const [loading, setLoading] = useState(true);

  // secondary CSV (colonial only)
  const [secondData, setSecondData] = useState([]);
  const [secondLoading, setSecondLoading] = useState(false);

  // load & filter summary_page.csv
  useEffect(() => {
    setLoading(true);
    Papa.parse('/data/summary_page.csv', {
      download: true,
      header: true,
      skipEmptyLines: true,
      complete: ({ data }) => {
        const rows = data.filter(
          (row) =>
            typeof row.surety_access === 'string' &&
            row.surety_access.trim().toLowerCase() === partnerFilter
        );

        const mapped = rows.map((item) => ({
          bondNumber: item['Bond Number']?.trim(),
          bondAmount:
            parseFloat(
              item['Bond Amount']?.replace(/\$/g, '').replace(/,/g, '').trim()
            ) || 0,
          principalName: item['Principal Name']?.trim(),
          ownershipCompany: item['Ownership Name']?.trim(),
          performanceYear:
            parseInt(item['Performance Year']?.trim(), 10) || '',
          bondType: item['Bond Type']?.trim(),
          rate: item['Rate']?.trim(),
          ratePercentage:
            parseFloat(
              item['Rate Percentage']?.replace(/[^0-9.]/g, '').trim()
            ) || 0,
        }));
        setBondData(mapped);
        setLoading(false);
      },
      error: () => setLoading(false),
    });
  }, [partnerFilter]);

  // load colonial_second_summary.csv if colonial
  useEffect(() => {
    if (partnerFilter === 'colonial') {
      setSecondLoading(true);
      Papa.parse('/data/colonial_second_summary.csv', {
        download: true,
        header: true,
        skipEmptyLines: true,
        complete: ({ data }) => {
          const mapped = data.map((item) => ({
            surety: item['Surety']?.trim(),
            bondAmount:
              parseFloat(item['Bond Amount']?.replace(/[$,]/g, '').trim()) ||
              0,
            sparxScore: item['Sparx Score']?.trim(),
            dateClosed: item['Date Closed']?.trim(),
          }));
          setSecondData(mapped);
          setSecondLoading(false);
        },
        error: () => setSecondLoading(false),
      });
    } else {
      setSecondData([]);
    }
  }, [partnerFilter]);

  const handleSort = (col) => {
    if (orderBy === col) setOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    else {
      setOrderBy(col);
      setOrder('asc');
    }
  };

  const sortedData = stableSort(bondData, getComparator(order, orderBy));

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant='h5' fontWeight='bold' mb={3}>
        {partnerFilter
          ? `Summary of Bonds for ${
              partnerFilter.charAt(0).toUpperCase() + partnerFilter.slice(1)
            }`
          : 'Summary of Bonds'}
      </Typography>

      <Paper sx={{ p: 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer sx={{ maxHeight: 500 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                    {[
                      { id: 'bondNumber', label: 'Bond Number' },
                      { id: 'bondAmount', label: 'Bond Amount' },
                      { id: 'principalName', label: 'Principal Name' },
                      { id: 'ownershipCompany', label: 'Ownership Name' },
                      { id: 'performanceYear', label: 'Performance Year' },
                      { id: 'bondType', label: 'Bond Type' },
                      { id: 'rate', label: 'Rate' },
                      { id: 'ratePercentage', label: 'Rate Percentage' },
                    ].map((col) => (
                      <TableCell
                        key={col.id}
                        sortDirection={orderBy === col.id ? order : false}
                      >
                        <TableSortLabel
                          active={orderBy === col.id}
                          direction={orderBy === col.id ? order : 'asc'}
                          onClick={() => handleSort(col.id)}
                        >
                          {col.label}
                        </TableSortLabel>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {sortedData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align='center'>
                        No data available.
                      </TableCell>
                    </TableRow>
                  ) : (
                    sortedData.map((row, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{row.bondNumber}</TableCell>
                        <TableCell>
                          ${row.bondAmount.toLocaleString()}
                        </TableCell>
                        <TableCell>{row.principalName}</TableCell>
                        <TableCell>{row.ownershipCompany}</TableCell>
                        <TableCell>{row.performanceYear}</TableCell>
                        <TableCell>
                          <Chip
                            label={row.bondType}
                            sx={{
                              backgroundColor:
                                row.bondType === 'KCC'
                                  ? '#f5e1fc'
                                  : row.bondType === 'MSSP'
                                  ? '#e1f3fc'
                                  : row.bondType === 'Non-CMS'
                                  ? '#dff2ed'
                                  : '#ffe0cc',
                              fontWeight: 500,
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={row.rate}
                            sx={{
                              backgroundColor:
                                row.rate === 'Risk Related'
                                  ? '#d6fdd6'
                                  : row.rate === 'N A'
                                  ? '#fff7d6'
                                  : '#eee',
                              fontWeight: 500,
                            }}
                          />
                        </TableCell>
                        <TableCell>{row.ratePercentage}%</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Box
              sx={{ mt: 1, display: 'flex', justifyContent: 'space-between' }}
            >
              <Typography variant='body2' color='textSecondary'>
                {sortedData.length} result
                {sortedData.length !== 1 && 's'}
              </Typography>
            </Box>
          </>
        )}

        {/* ─── Colonial Second CSV ─── */}
        {partnerFilter === 'colonial' && (
          <>
            <Typography variant='h6' fontWeight='bold' mt={4}>
              Colonial Secondary Summary
            </Typography>

            {secondLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <TableContainer sx={{ maxHeight: 300 }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                        <TableCell>Surety</TableCell>
                        <TableCell>Bond Amount</TableCell>
                        <TableCell>Sparx Score</TableCell>
                        <TableCell>Date Closed</TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {secondData.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4} align='center'>
                            No data available.
                          </TableCell>
                        </TableRow>
                      ) : (
                        secondData.map((row, idx) => (
                          <TableRow key={idx}>
                            <TableCell>{row.surety}</TableCell>
                            <TableCell>
                              ${row.bondAmount.toLocaleString()}
                            </TableCell>
                            <TableCell>{row.sparxScore}</TableCell>
                            <TableCell>{row.dateClosed}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Box
                  sx={{ mt: 1, display: 'flex', justifyContent: 'space-between' }}
                >
                  <Typography variant='body2' color='textSecondary'>
                    {secondData.length} result
                    {secondData.length !== 1 && 's'}
                  </Typography>
                </Box>
              </>
            )}
          </>
        )}
      </Paper>
    </Box>
  );
};

export default Summary;
